/**
 * Store global Svelte 5 (runes): embudo de ideas + archivo GTD heredado.
 * Toda mutación de ideas pasa por lib/idea.ts (reglas de autoridad) con autor 'carlos'.
 */

import type { GtdItem, SyncConfig, SyncRecord, ViewName } from './types';
import { getAllRecords, putLocal, getSyncConfig, setSyncConfig, getAccountKey, setAccountKey, genId } from './db';
import { deriveAccountKey, hashPassword } from './crypto';
import { sync, migrateToV2, type SyncResult, type SyncStatus } from './sync';
import {
  isIdea,
  capturar,
  editar,
  cambiarEstado,
  promover,
  rechazarPropuesta,
  restaurar,
  marcarRevisado,
  borrar,
  type Idea,
  type PatchIdea,
  type EstadoIdea,
  type Destino
} from './idea';

const AUTO_SYNC_MS = 30_000;

class Store {
  records = $state<SyncRecord[]>([]);
  view = $state<ViewName>('ideas');
  syncStatus = $state<SyncStatus>('no-config');
  lastSyncMessage = $state('');
  legacyPending = $state(0);
  syncConfig = $state<SyncConfig | null>(null);
  unlocked = $state(false);

  ideas = $derived(this.records.filter((r): r is Idea => isIdea(r) && !r.deleted));
  archivo = $derived(this.records.filter((r): r is GtdItem => !isIdea(r) && !r.deleted));
  pendientes = $derived(this.ideas.filter((i) => i.pendienteRevision));

  private key: CryptoKey | null = null;
  /** Solo en memoria durante la sesión desbloqueada (para leer payloads v1). Nunca se persiste. */
  private password: string | null = null;
  /** Escrituras locales: si cambia durante un sync, la relectura de IndexedDB pudo quedar vieja. */
  private writes = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private syncing = false;

  async init() {
    this.records = await getAllRecords();
    this.syncConfig = (await getSyncConfig()) ?? null;
    this.key = (await getAccountKey()) ?? null;
    if (!this.syncConfig?.code) {
      this.syncStatus = 'no-config';
      return;
    }
    this.unlocked = !!this.key;
    this.syncStatus = this.key ? 'idle' : 'locked';
    if (this.key) this.startAutoSync();
  }

  // ---- Sync ----
  async saveSyncConfig(cfg: SyncConfig) {
    await setSyncConfig(cfg);
    this.syncConfig = cfg;
    this.syncStatus = this.key ? 'idle' : 'locked';
  }

  /**
   * Verifica la password, deriva la clave de cuenta y la guarda (no extraíble) en IndexedDB.
   * Si el primer sync no logra descifrar nada de lo que baja, se trata como password/código
   * equivocados: se borra la clave y queda bloqueado.
   */
  async unlock(password: string): Promise<boolean> {
    if (!this.syncConfig) return false;
    const hash = await hashPassword(password, this.syncConfig.deviceId);
    if (!this.syncConfig.passwordHash) {
      const cfg = { ...$state.snapshot(this.syncConfig), passwordHash: hash };
      await setSyncConfig(cfg);
      this.syncConfig = cfg;
    } else if (hash !== this.syncConfig.passwordHash) {
      this.lastSyncMessage = 'Password incorrecta';
      return false;
    }
    const key = await deriveAccountKey(password, this.syncConfig.code);
    this.key = key;
    this.password = password;
    await setAccountKey(key);
    this.unlocked = true;
    const r = await this.runSync();
    if (r && r.failed > 0 && r.pulled === 0) {
      this.key = null;
      this.password = null;
      await setAccountKey(null);
      this.unlocked = false;
      this.syncStatus = 'locked';
      this.lastSyncMessage = `No se pudo descifrar ninguno de los ${r.failed} registros: ¿password o código distintos?`;
      return false;
    }
    this.startAutoSync();
    return true;
  }

  async lock() {
    this.stopAutoSync();
    this.key = null;
    this.password = null;
    await setAccountKey(null);
    this.unlocked = false;
    this.syncStatus = 'locked';
  }

  async runSync(password?: string): Promise<SyncResult | null> {
    if (!this.syncConfig || !this.key || this.syncing) return null;
    this.syncing = true;
    this.syncStatus = 'syncing';
    try {
      const writes = this.writes;
      const r = await sync($state.snapshot(this.syncConfig), {
        key: this.key,
        password: password ?? this.password ?? undefined
      });
      await this.reload(writes);
      this.legacyPending = r.legacyPending;
      if (r.failed > 0) {
        this.syncStatus = 'error';
        this.lastSyncMessage = `${r.failed} registros no se pudieron descifrar (¿password o código distintos?)`;
      } else {
        this.syncStatus = 'ok';
        this.lastSyncMessage = `Push ${r.pushed}, pull ${r.pulled}` + (r.legacyPending ? `, ${r.legacyPending} con cifrado viejo` : '');
      }
      return r;
    } catch (e) {
      this.syncStatus = 'error';
      this.lastSyncMessage = e instanceof Error ? e.message : String(e);
      return null;
    } finally {
      this.syncing = false;
    }
  }

  async migrate(password: string) {
    if (!this.syncConfig || !this.key) throw new Error('Desbloqueá primero');
    if (this.syncing) throw new Error('Hay una sincronización en curso: probá de nuevo en unos segundos');
    this.syncing = true;
    this.syncStatus = 'syncing';
    try {
      const writes = this.writes;
      const r = await migrateToV2($state.snapshot(this.syncConfig), { key: this.key, password });
      await this.reload(writes);
      this.password = password;
      this.legacyPending = r.legacyPending;
      this.syncStatus = 'ok';
      this.lastSyncMessage = `Migración v2: ${r.pushed} registros re-cifrados`;
    } catch (e) {
      this.syncStatus = 'error';
      this.lastSyncMessage = e instanceof Error ? e.message : String(e);
      throw e;
    } finally {
      this.syncing = false;
    }
  }

  /** Relee IndexedDB; si hubo escrituras locales mientras tanto, vuelve a leer (máx. 3). */
  private async reload(writes: number) {
    for (let n = 0; n < 3; n++) {
      const recs = await getAllRecords();
      this.records = recs;
      if (this.writes === writes) return;
      writes = this.writes;
    }
  }

  private startAutoSync() {
    this.stopAutoSync();
    this.timer = setInterval(() => {
      if (document.visibilityState === 'visible') void this.runSync();
    }, AUTO_SYNC_MS);
    window.addEventListener('focus', this.onFocus);
  }

  private stopAutoSync() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    window.removeEventListener('focus', this.onFocus);
  }

  private onFocus = () => void this.runSync();

  // ---- Ideas (autor: carlos) ----
  private async guardar(next: SyncRecord) {
    // IndexedDB no clona proxies de $state: siempre se escribe un objeto plano.
    const plain = $state.snapshot(next) as SyncRecord;
    await putLocal(plain);
    this.writes++;
    const i = this.records.findIndex((r) => r.id === plain.id);
    if (i === -1) this.records = [plain, ...this.records];
    else this.records[i] = plain;
    void this.runSync();
  }

  /** Copia plana de la idea (los registros en memoria son proxies de $state). */
  private idea(id: string): Idea {
    const i = this.ideas.find((x) => x.id === id);
    if (!i) throw new Error('Idea no encontrada');
    return $state.snapshot(i) as Idea;
  }

  async capturarIdea(texto: string, canal = 'tablero', ref = '') {
    return this.guardar(capturar({ texto, canal, ref }, 'carlos', Date.now(), genId()));
  }

  /** `version`: el updatedAt con el que se abrió la edición; si la idea cambió → conflict. */
  async editarIdea(id: string, patch: PatchIdea, version?: number) {
    return this.guardar(editar(this.idea(id), 'carlos', patch, '', Date.now(), version));
  }

  async cambiarEstadoIdea(id: string, nuevo: EstadoIdea) {
    return this.guardar(cambiarEstado(this.idea(id), 'carlos', nuevo, '', Date.now()));
  }

  async promoverIdea(id: string, destino: Destino) {
    return this.guardar(promover(this.idea(id), 'carlos', destino, Date.now()));
  }

  async aceptarPropuesta(id: string) {
    const i = this.idea(id);
    if (!i.propuesta) throw new Error('Sin propuesta');
    return this.guardar(promover(i, 'carlos', { tipo: i.propuesta.tipo, ref: i.propuesta.ref }, Date.now()));
  }

  async rechazarPropuestaIdea(id: string) {
    return this.guardar(rechazarPropuesta(this.idea(id), 'carlos', Date.now()));
  }

  async restaurarIdea(id: string, index: number) {
    return this.guardar(restaurar(this.idea(id), 'carlos', index, Date.now()));
  }

  async marcarRevisadoIdea(id: string) {
    const i = this.idea(id);
    const next = marcarRevisado(i, 'carlos', Date.now());
    if (next !== i) await this.guardar(next);
  }

  async borrarIdea(id: string) {
    return this.guardar(borrar(this.idea(id), 'carlos', Date.now()));
  }

  /** Archivo GTD → embudo. El ítem heredado no se modifica (RF-10). */
  async enviarAlEmbudo(item: GtdItem) {
    return this.guardar(capturar({ texto: item.text, notas: item.notes, canal: 'archivo-gtd', ref: item.id }, 'carlos', Date.now(), genId()));
  }

  yaEnEmbudo(itemId: string): boolean {
    return this.ideas.some((i) => i.origen.canal === 'archivo-gtd' && i.origen.ref === itemId);
  }

  setView(v: ViewName) {
    this.view = v;
  }
}

export const store = new Store();
